import type { ExprNode } from '@revelio/data-access';
import { PositionRangeKind, UserRangeKind } from '@revelio/data-access';

import { getFilterDefinition } from '../../engine/filterRegistry';
import type { RangeFilter } from '../../types/filters';
import { FilterId, FilterType } from '../../types/filters';
import { collectRngLeaves } from './expr';
import { upsertInstance } from './instances';

export const transformRangeLeavesInConjunct = ({
  conjunct,
  isNegated,
  groupRangeInstances,
}: {
  conjunct: ExprNode;
  isNegated: boolean;
  groupRangeInstances: Map<FilterId, RangeFilter[]>;
}): void => {
  // A "range leaf" is an ExprNode like:
  //   rng.pos_attribute = { kind: SalaryBase, start: 100000, end: 150000, curr: true }
  // or
  //   rng.user_attribute = { kind: GraduationYear, start: 2018, end: 2022 }
  const rngLeaves = collectRngLeaves(conjunct, isNegated);
  if (rngLeaves.length === 0) return;

  // Salary (Pay)
  const salaryRanges = rngLeaves
    .map((l) => {
      const pos = l.rng.pos_attribute;
      if (!pos) return null;
      if (
        pos.kind !== PositionRangeKind.SalaryBase &&
        pos.kind !== PositionRangeKind.SalaryTotal
      ) {
        return null;
      }
      return {
        start: pos.start,
        end: pos.end,
        curr: pos.curr,
        isNot: l.isNegated,
      };
    })
    .filter((r): r is NonNullable<typeof r> => r !== null);

  if (salaryRanges.length > 0) {
    // If multiple salary ranges appear in the same conjunct (often due to OR),
    // we coalesce them into a single Pay instance by taking:
    // - min = minimum start
    // - max = maximum end
    const starts = salaryRanges.map((r) => r.start).filter((n) => n > 0);
    const ends = salaryRanges.map((r) => r.end).filter((n) => n > 0);
    const min = starts.length > 0 ? Math.min(...starts) : undefined;
    const max = ends.length > 0 ? Math.max(...ends) : undefined;
    const isNot = salaryRanges.some((r) => r.isNot);
    const isCurrent = salaryRanges[0]?.curr ?? true;

    const definition = getFilterDefinition(FilterId.PAY);
    if (definition && definition.type === FilterType.RANGE) {
      upsertInstance(
        groupRangeInstances,
        FilterId.PAY,
        (f) =>
          f.id === FilterId.PAY &&
          (f.isCurrent ?? true) === isCurrent &&
          (f.isNot ?? false) === isNot,
        () => ({
          ...(definition as RangeFilter),
          min,
          max,
          isCurrent,
          isNot,
          isVisible: true,
          isUserSubmitted: true,
        }),
        (existing) => ({
          ...existing,
          min: min ?? existing.min,
          max: max ?? existing.max,
          isCurrent,
          isNot,
          isVisible: true,
          isUserSubmitted: true,
        })
      );
    }
  }

  // User ranges (Graduation year, Years of exp)
  const userRanges = rngLeaves
    .map((l) => {
      const user = l.rng.user_attribute;
      if (!user) return null;
      return { user, isNot: l.isNegated };
    })
    .filter((r): r is NonNullable<typeof r> => r !== null);

  for (const r of userRanges) {
    const { user, isNot } = r;
    const start = user.start > 0 ? user.start : undefined;
    const end = user.end > 0 ? user.end : undefined;

    const filterId = (() => {
      switch (user.kind) {
        case UserRangeKind.GraduationYear:
          return FilterId.GRADUATION_YEAR;
        case UserRangeKind.YearsOfExp:
          return FilterId.YEARS_OF_EXPERIENCE;
        default:
          return null;
      }
    })();
    if (!filterId) continue;

    const definition = getFilterDefinition(filterId);
    if (!definition || definition.type !== FilterType.RANGE) continue;

    upsertInstance(
      groupRangeInstances,
      filterId,
      (f) => f.id === filterId && (f.isNot ?? false) === isNot,
      () => ({
        ...(definition as RangeFilter),
        min: start,
        max: end,
        isNot,
        isVisible: true,
        isUserSubmitted: true,
      }),
      (existing) => ({
        ...existing,
        min: start ?? existing.min,
        max: end ?? existing.max,
        isNot,
        isVisible: true,
        isUserSubmitted: true,
      })
    );
  }
};
